import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { type ColumnDef } from '@tanstack/react-table'
import { Code2, ExternalLink, ImageIcon } from 'lucide-react'
import { AssetHoverPreview } from './AssetHoverPreview'
import { Button } from '@/components/ui/button'
import { DataTable, optionalColumn, selectionColumn } from '@/components/ui/data-table'
import { MutedCell, NameCell } from '@/components/TableCells'
import type { Merchant, RetailProduct } from '@/services/productFeed/productFeedApi'

/** The feed only gives an image URL, so any image type lets the hover preview show it. */
const IMAGE_MIME = 'image/*'

function ProductImageCell({ product }: { product: RetailProduct }) {
  return (
    <AssetHoverPreview
      src={product.imageLink}
      mimeType={IMAGE_MIME}
      caption={product.title}
      compact
    >
      <div className="flex size-14 items-center justify-center overflow-hidden rounded border bg-muted p-1">
        {product.imageLink ? (
          <img
            src={product.imageLink}
            alt=""
            className="block max-h-full max-w-full object-contain"
          />
        ) : (
          <ImageIcon className="size-4 opacity-35" />
        )}
      </div>
    </AssetHoverPreview>
  )
}

/** The price, with the regular price struck through beside a sale price. */
function PriceCell({ product }: { product: RetailProduct }) {
  if (product.salePrice && product.salePrice !== product.price) {
    return (
      <span className="flex flex-col text-xs tabular-nums">
        <span className="text-(--ad-text)">{product.salePrice}</span>
        <span className="text-(--ad-text-muted) line-through">{product.price}</span>
      </span>
    )
  }
  return <span className="text-xs tabular-nums text-(--ad-text)">{product.price || '—'}</span>
}

interface RetailProductTableProps {
  products: RetailProduct[]
  loading?: boolean
  /** Shown in a merchant column when the network imports more than one feed. */
  merchants: Merchant[]
  selection: RetailProduct[]
  onSelectionChange: (rows: RetailProduct[]) => void
  /** Offers the conversion tracking code for each product. */
  onConversionTags?: (product: RetailProduct) => void
  emptyMessage?: string
  columnsKey?: string
}

/** Products from the network's retail product feeds, for picking ones to advertise. */
export function RetailProductTable({
  products,
  loading,
  merchants,
  selection,
  onSelectionChange,
  onConversionTags,
  emptyMessage,
  columnsKey,
}: RetailProductTableProps) {
  const { t } = useTranslation()

  const columns = useMemo<ColumnDef<RetailProduct, unknown>[]>(() => {
    const merchantTitles = new Map(merchants.map((m) => [m.id, m.title || m.link]))
    return [
      selectionColumn<RetailProduct>(),
      {
        id: 'image',
        header: t('retailProducts.image'),
        enableSorting: false,
        size: 88,
        cell: ({ row }) => <ProductImageCell product={row.original} />,
      },
      {
        id: 'title',
        header: t('common.name'),
        enableSorting: false,
        size: 320,
        cell: ({ row }) => <NameCell name={row.original.title} id={row.original.itemId} />,
      },
      ...(merchants.length > 1
        ? [
            {
              id: 'merchant',
              header: t('retailProducts.merchant'),
              enableSorting: false,
              cell: ({ row }) => <MutedCell value={merchantTitles.get(row.original.merchantId)} />,
            } satisfies ColumnDef<RetailProduct, unknown>,
          ]
        : []),
      {
        id: 'brand',
        header: t('retailProducts.brand'),
        enableSorting: false,
        cell: ({ row }) => <MutedCell value={row.original.brand} />,
      },
      optionalColumn<RetailProduct>({
        id: 'colour',
        header: t('retailProducts.colour'),
        enableSorting: false,
        cell: ({ row }) => <MutedCell value={row.original.colour} />,
      }),
      optionalColumn<RetailProduct>({
        id: 'description',
        header: t('common.description'),
        enableSorting: false,
        size: 320,
        cell: ({ row }) => (
          <span className="line-clamp-2 text-xs text-(--ad-text-muted)">
            {row.original.description || '—'}
          </span>
        ),
      }),
      {
        id: 'price',
        header: t('retailProducts.price'),
        enableSorting: false,
        cell: ({ row }) => <PriceCell product={row.original} />,
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        enableHiding: false,
        size: onConversionTags ? 96 : 56,
        cell: ({ row }) => (
          <span className="flex items-center justify-end gap-1">
            {onConversionTags && (
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                title={t('retailProducts.conversionTags')}
                aria-label={t('retailProducts.conversionTags')}
                onClick={() => onConversionTags(row.original)}
              >
                <Code2 className="size-4" />
              </Button>
            )}
            {row.original.link && (
              <Button
                variant="ghost"
                size="icon-sm"
                asChild
                title={t('retailProducts.openProduct')}
              >
                <a
                  href={row.original.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={t('retailProducts.openProduct')}
                >
                  <ExternalLink className="size-4" />
                </a>
              </Button>
            )}
          </span>
        ),
      },
    ]
  }, [merchants, onConversionTags, t])

  return (
    <DataTable
      columns={columns}
      data={products}
      loading={loading}
      emptyMessage={emptyMessage ?? t('retailProducts.noProducts')}
      selection={selection}
      onSelectionChange={onSelectionChange}
      getRowId={(product) => product.key}
      columnsKey={columnsKey}
      downloadable={false}
    />
  )
}
